/**
 * Media / image URL helpers for SPA surfaces.
 * Keep static defaults root-absolute so nested routes (e.g. /kids/...) resolve correctly.
 */

export const DEFAULT_MEDIA_PLACEHOLDER = "/assets/img/placeholder.gif";

/**
 * Normalize API or stored media URLs for <img src> / CSS backgroundImage.
 * - Empty / null / "undefined" / "null" → placeholder
 * - Absolute http(s) URLs returned as-is (trimmed)
 * - Protocol-relative //host/path → https:
 * - Root-relative /path → as-is
 * - Bare relative path → leading /
 *
 * @param {string|null|undefined} url
 * @param {string} [fallback=DEFAULT_MEDIA_PLACEHOLDER]
 * @returns {string}
 */
export function resolveMediaUrl(url, fallback = DEFAULT_MEDIA_PLACEHOLDER) {
  if (url === null || url === undefined) {
    return fallback;
  }

  const trimmed = String(url).trim();
  if (
    !trimmed ||
    trimmed === "undefined" ||
    trimmed === "null" ||
    trimmed.toLowerCase() === "false"
  ) {
    return fallback;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  if (trimmed.indexOf("//") === 0) {
    return "https:" + trimmed;
  }

  if (trimmed.charAt(0) === "/") {
    return trimmed;
  }

  return "/" + trimmed.replace(/^\.\//, "");
}

/**
 * CSS background-image value with safe fallback.
 * @param {string|null|undefined} url
 * @param {string} [fallback=DEFAULT_MEDIA_PLACEHOLDER]
 * @returns {string}
 */
export function resolveMediaBackground(url, fallback = DEFAULT_MEDIA_PLACEHOLDER) {
  const resolved = resolveMediaUrl(url, fallback);
  // Quote CSS url() so Android Chrome does not drop backgrounds with query
  // strings, spaces, or parentheses in CDN paths.
  const escaped = String(resolved).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  return 'url("' + escaped + '")';
}

/**
 * Swap a broken <img> to the placeholder (Shop-style onError).
 * @param {React.SyntheticEvent} event
 * @param {string} [fallback=DEFAULT_MEDIA_PLACEHOLDER]
 */
export function handleMediaImgError(event, fallback = DEFAULT_MEDIA_PLACEHOLDER) {
  const img = event && event.currentTarget;
  if (!img || img.dataset.mediaFallbackApplied === "1") {
    return;
  }
  img.dataset.mediaFallbackApplied = "1";
  img.src = fallback;
  img.srcset = "";
}
